import { readFile } from "node:fs/promises";
import { parseArgs } from "node:util";

import packageMetadata from "../package.json" with { type: "json" };

import { Q5MClient, Q5MError } from "./client.js";
import {
  clearAuthentication,
  loadConfig,
  saveAuthentication,
  saveContext,
  resolveLoginApiUrl,
  type ConfigOptions,
  type ResolvedConfig,
} from "./config.js";
import {
  authorizeWithDeviceFlow,
  DeviceAuthorizationError,
  openBrowser,
  revokeDeviceCredential,
  type DeviceAuthorization,
} from "./device-auth.js";
import type { SecretInput, SecretOutput } from "./secret.js";
import type { Catalog, JsonObject, ToolDefinition } from "./types.js";

const HELP = `q5m - direct q5m agent-network access

Usage:
  q5m auth login                    Sign in through your browser
  q5m auth status [--json]          Check the active credential
  q5m auth logout                   Remove the saved credential
  q5m context [personal|GROUP_ID]   Show or select the default Home
  q5m catalog [--json]              Show Q instructions and complete tool catalog
  q5m tools [AGENT] [--json]        Show Q tools or a peer's domain tools
  q5m agents [--json]               List reachable installed agents
  q5m agent SLUG [--json]           Load one agent's instructions and context
  q5m route QUERY [--json]          Find agents that own a capability
  q5m recall QUERY [--limit N]      Search prior q5m conversations
  q5m call TOOL [options]           Call any Q catalog tool
  q5m tool AGENT TOOL [options]     Call one peer domain tool

Call options:
  --input JSON                      Tool arguments as a JSON object
  --input-file FILE                 Read tool arguments from FILE (use - for stdin)
  --group personal|GROUP_ID         Override the configured host Home
  --home personal|GROUP_ID          Peer Home for the 'tool' shorthand
  --json, -j                        Force machine-readable JSON output

Authentication:
  Run 'q5m auth login', then approve the displayed code at https://platform.q5m.ai/auth/device.
  Login uses production by default. Development login requires Q5M_API_URL plus Q5M_ALLOW_DEVELOPMENT_LOGIN=1.
  Q5M_API_KEY, Q5M_API_URL, and Q5M_GROUP_ID override saved configuration.
`;

interface WritableOutput extends SecretOutput {
  isTTY?: boolean;
}

export interface CliIO {
  stdin: SecretInput;
  stdout: WritableOutput;
  stderr: WritableOutput;
}

export interface RunCliOptions {
  io?: CliIO;
  config?: ConfigOptions;
  fetch?: typeof globalThis.fetch;
  openBrowser?: (url: string) => Promise<boolean>;
  sleep?: (milliseconds: number) => Promise<void>;
  now?: () => number;
}

interface ParsedOptions {
  json?: boolean;
  group?: string;
  input?: string;
  inputFile?: string;
  home?: string;
  limit?: string;
  help?: boolean;
  version?: boolean;
}

function parseCommandLine(argv: string[]): { positionals: string[]; options: ParsedOptions } {
  try {
    const parsed = parseArgs({
      args: argv,
      allowPositionals: true,
      strict: true,
      options: {
        json: { type: "boolean", short: "j" },
        group: { type: "string" },
        input: { type: "string" },
        "input-file": { type: "string" },
        home: { type: "string" },
        limit: { type: "string" },
        help: { type: "boolean", short: "h" },
        version: { type: "boolean", short: "v" },
      },
    });
    return {
      positionals: parsed.positionals,
      options: {
        json: parsed.values.json,
        group: parsed.values.group,
        input: parsed.values.input,
        inputFile: parsed.values["input-file"],
        home: parsed.values.home,
        limit: parsed.values.limit,
        help: parsed.values.help,
        version: parsed.values.version,
      },
    };
  } catch (error) {
    throw new Q5MError(
      error instanceof Error ? error.message : String(error),
      0,
      "usage_error",
    );
  }
}

function writeJson(output: WritableOutput, value: unknown): void {
  output.write(`${JSON.stringify(value)}\n`);
}

function writeHuman(output: WritableOutput, value: unknown): void {
  if (typeof value === "string") {
    output.write(`${value}\n`);
    return;
  }
  output.write(`${JSON.stringify(value, null, 2)}\n`);
}

function printTools(output: WritableOutput, tools: ToolDefinition[]): void {
  for (const tool of tools) {
    const flags = [
      tool.annotations.readOnlyHint ? "read" : "write",
      ...(tool.annotations.destructiveHint ? ["destructive"] : []),
    ].join(",");
    output.write(`${tool.name}\t[${flags}]\t${tool.description}\n`);
  }
}

function requiredPositionals(positionals: string[], count: number, usage: string): string[] {
  if (positionals.length !== count) {
    throw new Q5MError(`Usage: ${usage}`, 0, "usage_error");
  }
  return positionals;
}

async function readJsonInput(
  options: ParsedOptions,
  input: SecretInput,
): Promise<JsonObject> {
  if (options.input !== undefined && options.inputFile !== undefined) {
    throw new Q5MError("Use only one of --input or --input-file", 0, "usage_error");
  }
  let source = options.input;
  if (options.inputFile !== undefined) {
    if (options.inputFile === "-") {
      if (!input[Symbol.asyncIterator]) {
        throw new Q5MError("stdin is unavailable", 0, "usage_error");
      }
      source = "";
      for await (const chunk of input as AsyncIterable<Buffer | string>) {
        source += chunk.toString();
      }
    } else {
      try {
        source = await readFile(options.inputFile, "utf8");
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        throw new Q5MError(
          `Unable to read input file '${options.inputFile}': ${detail}`,
          0,
          "usage_error",
        );
      }
    }
  }
  if (source === undefined || source.trim() === "") return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch (error) {
    throw new Q5MError(`Invalid JSON input: ${(error as Error).message}`, 0, "usage_error");
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Q5MError("Tool input must be a JSON object", 0, "usage_error");
  }
  return parsed as JsonObject;
}

function clientFor(
  config: ResolvedConfig,
  groupOverride: string | undefined,
  fetchImplementation: typeof globalThis.fetch | undefined,
): Q5MClient {
  if (!config.apiKey) {
    throw new Q5MError(
      "No q5m API key. Run 'q5m auth login' or set Q5M_API_KEY.",
      401,
      "not_authenticated",
    );
  }
  const selectedGroup = groupOverride ?? config.context;
  return new Q5MClient({
    apiUrl: config.apiUrl,
    apiKey: config.apiKey,
    ...(selectedGroup === "personal" ? {} : { groupId: selectedGroup }),
    ...(fetchImplementation ? { fetch: fetchImplementation } : {}),
  });
}

const AUTHORIZATION_ERROR_CODES = new Set([
  "ambiguous_entry",
  "context_not_allowed",
  "no_entry",
  "not_agent_admin",
  "not_group_member",
  "not_installed",
  "not_pool_admin",
  "not_reachable",
]);

function requireToolSuccess(output: unknown): unknown {
  if (output === null || typeof output !== "object" || Array.isArray(output)) return output;
  const record = output as Record<string, unknown>;
  if (typeof record.error !== "string") return output;

  const explicitCode =
    typeof record.code === "string" && /^[a-z][a-z0-9_]*$/.test(record.code)
      ? record.code
      : undefined;
  const errorCode = /^[a-z][a-z0-9_]*$/.test(record.error) ? record.error : undefined;
  const code = explicitCode ?? errorCode ?? "tool_error";
  const message = typeof record.message === "string" ? record.message : record.error;
  const status = AUTHORIZATION_ERROR_CODES.has(code) ? 403 : 0;
  throw new Q5MError(message, status, code, record.status === "partial" ? record : undefined);
}

async function callForCommand(
  client: Q5MClient,
  name: string,
  arguments_: JsonObject = {},
): Promise<unknown> {
  return requireToolSuccess(await client.callTool(name, arguments_));
}

function resultMode(io: CliIO, forcedJson: boolean | undefined): "json" | "human" {
  return forcedJson || !io.stdout.isTTY ? "json" : "human";
}

function printResult(io: CliIO, mode: "json" | "human", value: unknown): void {
  if (mode === "json") writeJson(io.stdout, value);
  else writeHuman(io.stdout, value);
}
function printCatalog(
  io: CliIO,
  mode: "json" | "human",
  catalog: Catalog,
): void {
  if (mode === "json") writeJson(io.stdout, catalog);
  else {
    io.stdout.write(`${catalog.agent.name} (${catalog.agent.slug}) @ ${catalog.commit}\n`);
    io.stdout.write(`Context: ${catalog.context}\n\n`);
    printTools(io.stdout, catalog.tools);
  }
}

async function handleAuth(
  args: string[],
  options: ParsedOptions,
  io: CliIO,
  runOptions: RunCliOptions,
): Promise<number> {
  const action = args[0];
  const mode = resultMode(io, options.json);
  const structuredProgress = options.json || !io.stderr.isTTY;
  if (action === "login") {
    requiredPositionals(args, 1, "q5m auth login");
    const environment = runOptions.config?.env ?? process.env;
    if (environment.Q5M_API_KEY?.trim()) {
      throw new Q5MError(
        "Unset Q5M_API_KEY before q5m auth login; environment credentials override saved authentication.",
        0,
        "usage_error",
      );
    }
    const current = await loadConfig(runOptions.config);
    const apiUrl = resolveLoginApiUrl(environment);
    const apiKey = await authorizeWithDeviceFlow({
      apiUrl,
      clientId: "q5m-cli",
      ...(runOptions.fetch ? { fetch: runOptions.fetch } : {}),
      ...(runOptions.sleep ? { sleep: runOptions.sleep } : {}),
      ...(runOptions.now ? { now: runOptions.now } : {}),
      onVerification: async (authorization: DeviceAuthorization) => {
        if (!structuredProgress) {
          io.stderr.write(
            `Open ${authorization.verification_uri}\nEnter code: ${authorization.user_code}\n`,
          );
        }
        const opened = await (runOptions.openBrowser ?? openBrowser)(
          authorization.verification_uri_complete ?? authorization.verification_uri,
        );
        if (structuredProgress) {
          writeJson(io.stderr, {
            event: "device_authorization",
            verification_uri: authorization.verification_uri,
            verification_uri_complete:
              authorization.verification_uri_complete ?? authorization.verification_uri,
            user_code: authorization.user_code,
            browser_opened: opened,
          });
        } else if (opened) {
          io.stderr.write("Your browser has been opened.\n");
        }
      },
    });
    await saveAuthentication(apiUrl, apiKey, runOptions.config);
    const client = new Q5MClient({
      apiUrl,
      apiKey,
      ...(current.groupId ? { groupId: current.groupId } : {}),
      ...(runOptions.fetch ? { fetch: runOptions.fetch } : {}),
    });
    const catalog = await client.getCatalog();
    await saveContext(catalog.context, runOptions.config);
    printResult(io, mode, {
      authenticated: true,
      api_url: client.apiUrl,
      agent: catalog.agent.slug,
      context: catalog.context,
    });
    return 0;
  }

  if (action === "status") {
    requiredPositionals(args, 1, "q5m auth status [--json]");
    const config = await loadConfig(runOptions.config);
    const client = clientFor(config, options.group, runOptions.fetch);
    const catalog = await client.getCatalog();
    printResult(io, mode, {
      authenticated: true,
      api_url: client.apiUrl,
      credential_source: config.credentialSource,
      context: catalog.context,
      commit: catalog.commit,
    });
    return 0;
  }

  if (action === "logout") {
    requiredPositionals(args, 1, "q5m auth logout");
    const current = await loadConfig(runOptions.config);
    if (current.credentialSource === "config" && current.apiKey) {
      await revokeDeviceCredential(
        current.apiUrl,
        current.apiKey,
        runOptions.fetch ?? globalThis.fetch,
      );
    }
    await clearAuthentication(runOptions.config);
    const environmentActive = Boolean((runOptions.config?.env ?? process.env).Q5M_API_KEY);
    printResult(io, mode, {
      authenticated: environmentActive,
      saved_credential_removed: true,
      ...(environmentActive ? { note: "Q5M_API_KEY remains active in the environment" } : {}),
    });
    return 0;
  }

  throw new Q5MError("Usage: q5m auth <login|status|logout>", 0, "usage_error");
}

async function handleContext(
  args: string[],
  options: ParsedOptions,
  io: CliIO,
  runOptions: RunCliOptions,
): Promise<number> {
  if (args.length > 1) {
    throw new Q5MError("Usage: q5m context [personal|GROUP_ID]", 0, "usage_error");
  }
  const config = await loadConfig(runOptions.config);
  const mode = resultMode(io, options.json);
  if (args.length === 0) {
    printResult(io, mode, { context: config.context });
    return 0;
  }

  const requested = args[0]?.trim() || "personal";
  const client = clientFor(config, requested, runOptions.fetch);
  const catalog = await client.getCatalog();
  if (catalog.context !== requested) {
    throw new Q5MError(
      `q5m did not authorize the '${requested}' Home`,
      403,
      "not_group_member",
    );
  }
  await saveContext(requested, runOptions.config);
  printResult(io, mode, { context: requested });
  return 0;
}

async function executeCommand(
  command: string,
  args: string[],
  options: ParsedOptions,
  io: CliIO,
  runOptions: RunCliOptions,
): Promise<number> {
  if (command === "auth") return handleAuth(args, options, io, runOptions);
  if (command === "context") return handleContext(args, options, io, runOptions);

  const config = await loadConfig(runOptions.config);
  const client = clientFor(config, options.group, runOptions.fetch);
  const mode = resultMode(io, options.json);

  if (command === "catalog") {
    requiredPositionals(args, 0, "q5m catalog [--json]");
    printCatalog(io, mode, await client.getCatalog());
    return 0;
  }

  if (command === "tools") {
    if (args.length > 1) throw new Q5MError("Usage: q5m tools [AGENT]", 0, "usage_error");
    if (args.length === 0 || args[0] === "q") {
      const catalog = await client.getCatalog();
      if (mode === "json") writeJson(io.stdout, catalog.tools);
      else printTools(io.stdout, catalog.tools);
    } else {
      printResult(io, mode, await callForCommand(client, "list_tools", { agent: args[0] }));
    }
    return 0;
  }

  if (command === "agents") {
    requiredPositionals(args, 0, "q5m agents [--json]");
    printResult(io, mode, await callForCommand(client, "list_agents"));
    return 0;
  }

  if (command === "agent") {
    const [agent] = requiredPositionals(args, 1, "q5m agent SLUG [--json]");
    if (agent === "q") {
      printCatalog(io, mode, await client.getCatalog());
    } else {
      printResult(io, mode, await callForCommand(client, "get_agent", { agent }));
    }
    return 0;
  }

  if (command === "route") {
    if (args.length === 0) throw new Q5MError("Usage: q5m route QUERY", 0, "usage_error");
    printResult(io, mode, await callForCommand(client, "route", { query: args.join(" ") }));
    return 0;
  }

  if (command === "recall") {
    if (args.length === 0) throw new Q5MError("Usage: q5m recall QUERY", 0, "usage_error");
    const recallArguments: JsonObject = { query: args.join(" ") };
    if (options.limit !== undefined) {
      const limit = Number(options.limit);
      if (!Number.isInteger(limit)) {
        throw new Q5MError("--limit must be an integer", 0, "usage_error");
      }
      recallArguments.limit = limit;
    }
    printResult(io, mode, await callForCommand(client, "recall", recallArguments));
    return 0;
  }

  if (command === "call") {
    const tool = requiredPositionals(args, 1, "q5m call TOOL [options]")[0] as string;
    const input = await readJsonInput(options, io.stdin);
    printResult(io, mode, await callForCommand(client, tool, input));
    return 0;
  }

  if (command === "tool") {
    const [agent, tool] = requiredPositionals(args, 2, "q5m tool AGENT TOOL [options]") as [
      string,
      string,
    ];
    const input = await readJsonInput(options, io.stdin);
    const selectedHome = options.home;
    printResult(
      io,
      mode,
      await callForCommand(client, "call_tool", {
        agent,
        name: tool,
        arguments: input,
        ...(selectedHome ? { group: selectedHome } : {}),
      }),
    );
    return 0;
  }

  throw new Q5MError(`Unknown command: ${command}`, 0, "usage_error");
}

export async function runCli(argv: string[], options: RunCliOptions = {}): Promise<number> {
  const io = options.io ?? {
    stdin: process.stdin,
    stdout: process.stdout,
    stderr: process.stderr,
  };

  try {
    const parsed = parseCommandLine(argv);
    if (parsed.options.version) {
      io.stdout.write(`${packageMetadata.version}\n`);
      return 0;
    }
    if (parsed.options.help || parsed.positionals.length === 0) {
      io.stdout.write(HELP);
      return 0;
    }
    const [command, ...args] = parsed.positionals;
    if (!command) {
      io.stdout.write(HELP);
      return 0;
    }
    return await executeCommand(command, args, parsed.options, io, options);
  } catch (error) {
    const q5mError =
      error instanceof Q5MError
        ? error
        : error instanceof DeviceAuthorizationError
          ? new Q5MError(error.message, 0, error.code)
          : new Q5MError(error instanceof Error ? error.message : String(error), 0);
    const json = argv.includes("--json") || argv.includes("-j") || !io.stderr.isTTY;
    if (json) {
      writeJson(io.stderr, {
        error: {
          code: q5mError.code,
          message: q5mError.message,
          ...(q5mError.status ? { status: q5mError.status } : {}),
          ...(q5mError.result ? { result: q5mError.result } : {}),
        },
      });
    } else {
      io.stderr.write(`q5m: ${q5mError.message}\n`);
    }
    if (q5mError.code === "usage_error" || q5mError.code === "invalid_input") return 2;
    if (q5mError.status === 401 || q5mError.status === 403) return 3;
    return 1;
  }
}

